const _ = require("lodash");
const Font = require("../../util/Font");
const CanvasText = require("../../util/CanvasText");

const {
	findArguments,
	findOptionalArgument,
	splitArgIntoMathNodes,
} = require("../util/ParserHelpers");
const {
	drawArrowHead,
	calculateArrowHeadPositions,
} = require("../../util/ArrowDraw");
const {getLineWidth, getDigitWidth} = require("../util/RenderHelpers");

const arrowseq = function(ps) {
	const args = findArguments(ps, 2);
	if (!args) {
		return false;
	}
	const seqTerms = splitArgIntoMathNodes(args[0]);
	const labels = splitArgIntoMathNodes(args[1]);
	if (!seqTerms.length) {
		ps.pushError("Arrow sequences require at least one term.");
		return;
	}
	const labelsProcessed = [];
	const defaultLabel = labels[labels.length - 1] || {
		type: "math",
		children: [{type: "text", text: ""}],
	};
	const thirdArg = findOptionalArgument(ps) || {text: "right"};
	const directions = thirdArg.text.split(";");
	const directionsProcessed = [];
	const defaultDirection = directions[directions.length - 1];
	for (let i = 0; i < seqTerms.length - 1; i++) {
		const label = labels[i] || _.cloneDeep(defaultLabel);
		labelsProcessed.push(label);
		const direction = directions[i] || defaultDirection;
		directionsProcessed.push(direction);
	}
	return [
		{
			type: "arrowSequence",
			children: [
				{
					type: "math",
					children: seqTerms,
				},
				{
					type: "math",
					children: labelsProcessed,
				},
				{
					type: "list",
					children: directionsProcessed,
				},
			],
		},
	];
};

const arrowSequence = {
	draw(rc, x, y, node, measure) {
		const context = rc.context;
		const {
			fontInfo,
			maxSeqWidth,
			seqSizes,
			labelRatio,
			labelSizes,
			arrowHeight,
			maxSeqTop,
		} = measure;
		const seqTerms = node.children[0].children;
		const labels = node.children[1].children;
		const directions = node.children[2].children;
		const lineWidth = getLineWidth(rc, fontInfo);

		context.save();
		context.font = Font.fromInfo(fontInfo);
		context.lineWidth = lineWidth;
		const arrowPadding = lineWidth * 2;
		const spacing = context.measureText(",  ").width;
		let currX = x + (maxSeqWidth - seqSizes[0].width) / 2;
		const currY = y;
		rc.drawNode(rc, currX, currY, seqTerms[0]);
		currX -= (maxSeqWidth - seqSizes[0].width) / 2;

		const labelFontInfo = _.extend({}, fontInfo, {
			size: fontInfo.size * labelRatio,
		});
		let leftX, rightX, labelX, labelY;
		for (let i = 1; i < seqTerms.length; i++) {
			//drawing the terms
			leftX = currX + maxSeqWidth / 2 + arrowPadding;
			currX += maxSeqWidth + (maxSeqWidth - seqSizes[i].width) / 2;
			CanvasText.drawMiddleText(
				context,
				",  ",
				currX - (maxSeqWidth - seqSizes[i - 1].width) / 2,
				currY
			);
			currX += spacing;
			rc.drawNode(rc, currX, currY, seqTerms[i]);
			currX -= (maxSeqWidth - seqSizes[i].width) / 2;
			rightX = currX + maxSeqWidth / 2 - arrowPadding;

			//drawing the arrow's label
			context.save();
			labelX = (leftX + rightX) / 2 - labelSizes[i - 1].width / 2;
			labelY = currY - maxSeqTop - arrowHeight - labelSizes[i - 1].bottom;
			context.fontInfo = Font.fromInfo(labelFontInfo);
			rc.drawNode(rc, labelX, labelY - 2 * arrowPadding, labels[i - 1]);
			context.restore();

			if (directions[i - 1] !== "none") {
				//drawing the arrow's tail
				context.beginPath();
				context.moveTo(leftX, currY - maxSeqTop - arrowPadding);
				context.quadraticCurveTo(
					(leftX + rightX) / 2,
					labelY - arrowPadding,
					rightX,
					currY - maxSeqTop - arrowPadding
				);
				context.stroke();

				//drawing the arrow's head
				const headLength = arrowHeight / 2;
				const headHeight = (headLength * 2) / 3;
				const arrowPositions = calculateArrowHeadPositions({
					startX: (leftX + rightX) / 2,
					endX: directions[i - 1] === "left" ? leftX : rightX,
					startY: (currY - maxSeqTop - arrowHeight + labelY) / 2,
					endY: currY - maxSeqTop - arrowPadding,
					headLength,
					headHeight,
				});
				drawArrowHead(context, _.extend(arrowPositions, {curved: true}));
			}
		}
		context.restore();
	},
	measure(rc, fontInfo, props, node) {
		const context = rc.context;
		const seqSizes = [];
		let maxBottom = 0;
		let maxTop = 0;
		let maxWidth = 0;
		for (let i = 0; i < node.children[0].children.length; i++) {
			seqSizes[i] = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0].children[i]
			);
			if (seqSizes[i].top > maxTop) {
				maxTop = seqSizes[i].top;
			}
			if (seqSizes[i].bottom > maxBottom) {
				maxBottom = seqSizes[i].bottom;
			}
			if (seqSizes[i].width > maxWidth) {
				maxWidth = seqSizes[i].width;
			}
		}
		let seqWidth = maxWidth * node.children[0].children.length;
		const labelRatio = 0.7;
		const labelFontInfo = _.extend({}, fontInfo, {
			size: fontInfo.size * labelRatio,
		});
		const labelSizes = [];
		let maxLabelTop = 0;
		let maxLabelBottom = 0;
		for (let i = 0; i < node.children[1].children.length; i++) {
			labelSizes[i] = rc.measureNode(
				rc,
				labelFontInfo,
				props,
				node.children[1].children[i]
			);
			if (labelSizes[i].top > maxLabelTop) {
				maxLabelTop = labelSizes[i].top;
			}
			if (labelSizes[i].bottom > maxLabelBottom) {
				maxLabelBottom = labelSizes[i].bottom;
			}
			// Assuming that the label width is smaller than sequence width.
		}

		context.save();
		context.font = Font.fromInfo(fontInfo);
		const spacing = context.measureText(",  ").width;
		seqWidth += (seqSizes.length - 1) * spacing;

		const lineWidth = getLineWidth(rc, fontInfo);
		const arrowPadding = lineWidth * 2;

		const arrowHeight = getDigitWidth(rc, fontInfo);
		const totalTop =
			maxTop + arrowHeight + maxLabelBottom + maxLabelTop + 2 * arrowPadding;
		context.restore();
		return {
			width: seqWidth,
			top: totalTop,
			bottom: maxBottom,
			fontInfo,
			seqSizes,
			labelRatio,
			labelSizes,
			maxSeqWidth: maxWidth,
			maxSeqTop: maxTop,
			arrowHeight,
		};
	},
	readAloud: "",
};

module.exports = {
	parsers: {
		mathCommands: {
			arrowseq,
		},
	},
	nodes: {
		arrowSequence,
	},
};
